// Endurance prototypes at real scale (metres), lofted bodies. Generic designs,
// not any manufacturer's car (the GT and the hatchback are in sculpted.ts):
//   hypercar   top-class prototype, 5.1 × 2.0 × 1.06 m, fin and full-width wing
//   prototype  smaller prototype, 4.75 × 1.9 × 1.04 m
// Bodies are lofts (loft.ts); the livery is painted on the body sides with the
// class-colored number panel on the doors.
import * as THREE from 'three'
import { loft, plate, sampleSections, wingElement, type Section } from './loft'
import type { Livery, Placement } from './livery'
import { liveryTexture, materials, mirror, numberDecal, paintMaterial, wheel } from './parts'
import type { CarModel } from './index'

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z)

type Spec = {
  body: Section[] // the main body (sides carry the livery)
  cabin: Section[] // glass house
  roof?: Section[] // painted roof cap over the glass
  pillars?: number[] // z of painted window pillars (B/C pillars)
  fenders?: { front: Section[]; rear: Section[] } // bubble fenders (prototypes)
  axles: [number, number] // front z, rear z
  track: number // wheel centre x
  tyre: { radius: number; front: number; rear: number; rim: number; spokes: number }
  wing?: { span: number; chord: number; y: number; z: number; endplate: [number, number][]; necks?: boolean }
  fin?: [number, number][]
  lights: { front: [number, number, number, number, number]; rear: [number, number, number] } // front: x, y, z, w, h; rear: y, z, w
  mirrors: [number, number, number]
  placement: Placement
  coloredRims?: boolean
  splitter: [number, number] // width, z
}

const cache = new Map<string, THREE.BufferGeometry>()
const geo = (key: string, make: () => THREE.BufferGeometry) => {
  let g = cache.get(key)
  if (!g) {
    g = make()
    cache.set(key, g)
  }
  return g
}

const prototypeSpec = (scale: number, fin: boolean, wingSpan: number): Spec => {
  const z = (v: number) => v * scale
  return {
    body: [
      { z: z(-2.55), w: 0.88, h: 0.2, cy: 0.5, top: 4, bottom: 6 },
      { z: z(-2.1), w: 0.92, h: 0.26, cy: 0.52, top: 3.5, bottom: 6 },
      { z: z(-1.2), w: 0.86, h: 0.27, cy: 0.5, top: 3, bottom: 6 },
      { z: z(-0.3), w: 0.66, h: 0.3, cy: 0.5, top: 3, bottom: 6 },
      { z: z(0.6), w: 0.66, h: 0.28, cy: 0.48, top: 3, bottom: 6 },
      { z: z(1.4), w: 0.84, h: 0.22, cy: 0.42, top: 3, bottom: 6 },
      { z: z(2.1), w: 0.9, h: 0.16, cy: 0.33, top: 3.5, bottom: 6 },
      { z: z(2.55), w: 0.86, h: 0.1, cy: 0.24, top: 4, bottom: 6 },
    ],
    cabin: [
      { z: z(-1.2), w: 0.36, h: 0.08, cy: 0.78, top: 2.4, bottom: 4 },
      { z: z(-0.5), w: 0.5, h: 0.24, cy: 0.8, top: 2.2, bottom: 4 },
      { z: z(0.2), w: 0.54, h: 0.26, cy: 0.79, top: 2.2, bottom: 4 },
      { z: z(0.9), w: 0.5, h: 0.14, cy: 0.72, top: 2.2, bottom: 4 },
      { z: z(1.35), w: 0.42, h: 0.04, cy: 0.64, top: 2.2, bottom: 4 },
    ],
    roof: [
      { z: z(-0.75), w: 0.22, h: 0.03, cy: 1.02, top: 2, bottom: 2 },
      { z: z(-0.2), w: 0.3, h: 0.04, cy: 1.05, top: 2, bottom: 2 },
      { z: z(0.45), w: 0.28, h: 0.03, cy: 1.02, top: 2, bottom: 2 },
    ],
    fenders: {
      front: [
        { z: z(0.8), cx: 0.72, w: 0.08, h: 0.08, cy: 0.5 },
        { z: z(1.1), cx: 0.72, w: 0.2, h: 0.26, cy: 0.5, top: 2.6, bottom: 6 },
        { z: z(1.75), cx: 0.73, w: 0.21, h: 0.28, cy: 0.49, top: 2.6, bottom: 6 },
        { z: z(2.25), cx: 0.72, w: 0.19, h: 0.18, cy: 0.4, top: 2.6, bottom: 6 },
        { z: z(2.5), cx: 0.7, w: 0.14, h: 0.08, cy: 0.3 },
      ],
      rear: [
        { z: z(-2.56), cx: 0.72, w: 0.17, h: 0.22, cy: 0.55, top: 3, bottom: 6 },
        { z: z(-2.2), cx: 0.73, w: 0.21, h: 0.31, cy: 0.54, top: 2.6, bottom: 6 },
        { z: z(-1.25), cx: 0.73, w: 0.21, h: 0.31, cy: 0.53, top: 2.6, bottom: 6 },
        { z: z(-0.65), cx: 0.7, w: 0.17, h: 0.22, cy: 0.49, top: 2.6, bottom: 6 },
        { z: z(-0.15), cx: 0.66, w: 0.06, h: 0.08, cy: 0.45 },
      ],
    },
    axles: [z(1.55), z(-1.62)],
    track: 0.84,
    tyre: { radius: 0.36, front: 0.31, rear: 0.34, rim: 0.23, spokes: 6 },
    wing: { span: wingSpan, chord: 0.36, y: 1.02, z: z(-2.3), endplate: [[z(-1.95), 0.55], [z(-1.95), 1.08], [z(-2.1), 1.16], [z(-2.6), 1.16], [z(-2.62), 0.55]] },
    fin: fin ? [[z(-0.55), 1.04], [z(-2.15), 1.12], [z(-2.15), 0.76], [z(-0.95), 0.8]] : undefined,
    lights: { front: [0.7, 0.44, z(2.38), 0.26, 0.05], rear: [0.66, z(-2.56), 1.7] },
    mirrors: [0.72, 0.82, z(1.0)],
    placement: { numberU: 0.53, numberHeight: 0.55, numberSize: 0.22, nameU: 0.53, nameHeight: 0.2, nameSize: 0.1, aspect: 1.5 },
    splitter: [1.9, z(2.5)],
  }
}

const specs: Record<'hypercar' | 'prototype', Spec> = {
  hypercar: prototypeSpec(1, true, 1.92),
  prototype: { ...prototypeSpec(0.93, false, 1.7), track: 0.81 }
}

export function buildClosed(type: keyof typeof specs, livery: Livery): CarModel {
  const spec = specs[type]
  const car = new THREE.Group()
  const bodyTex = liveryTexture(livery, spec.placement)
  const trimTex = liveryTexture(livery, null)
  const body = paintMaterial(null, bodyTex)
  const trim = paintMaterial(null, trimTex)
  const primary = paintMaterial(livery.primary)
  const secondary = paintMaterial(livery.secondary)
  const lamps = new THREE.MeshBasicMaterial({ color: 0xe9e9e2 })
  const add = (g: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0) => {
    const mesh = new THREE.Mesh(g, m)
    mesh.position.set(x, y, z)
    mesh.castShadow = true
    car.add(mesh)
    return mesh
  }

  add(geo(`${type}-body`, () => loft(spec.body, { steps: 72, around: 56 })), body)
  add(geo(`${type}-cabin`, () => loft(spec.cabin, { steps: 40, around: 40 })), materials.glass)
  if (spec.roof) add(geo(`${type}-roof`, () => loft(spec.roof!, { steps: 16, around: 24 })), secondary)
  // Window pillars: painted bands across the glass house.
  if (spec.pillars) {
    const rows = sampleSections(spec.cabin, 80)
    for (const pz of spec.pillars) {
      const row = rows.reduce((a, b) => (Math.abs(b.z - pz) < Math.abs(a.z - pz) ? b : a))
      add(new THREE.BoxGeometry(row.w * 2 + 0.03, row.h * 2 + 0.02, 0.09), secondary, 0, row.cy + 0.005, pz)
    }
  }
  if (spec.fenders) {
    const f = spec.fenders
    add(geo(`${type}-ffr`, () => loft(f.front, { steps: 32, around: 36 })), trim)
    add(geo(`${type}-ffl`, () => loft(f.front, { steps: 32, around: 36, mirrorX: true })), trim)
    add(geo(`${type}-frr`, () => loft(f.rear, { steps: 32, around: 36 })), trim)
    add(geo(`${type}-frl`, () => loft(f.rear, { steps: 32, around: 36, mirrorX: true })), trim)
  }

  // Splitter, diffuser and floor.
  add(new THREE.BoxGeometry(spec.splitter[0], 0.02, 0.16), materials.carbon, 0, 0.09, spec.splitter[1])
  const rearZ = Math.min(...spec.body.map((s) => s.z))
  add(new THREE.BoxGeometry(spec.splitter[0] * 0.8, 0.12, 0.3), materials.carbon, 0, 0.18, rearZ + 0.1)
  for (let i = -3; i <= 3; i++) add(new THREE.BoxGeometry(0.015, 0.16, 0.34), materials.carbonMatte, i * 0.2, 0.2, rearZ + 0.08)

  // Front grille / intakes and lights.
  const frontZ = Math.max(...spec.body.map((s) => s.z))
  const [lx, ly, lz, lw, lh] = spec.lights.front
  for (const s of [1, -1]) {
    // Lens: a flattened capsule half sunk into the body.
    const lamp = add(new THREE.SphereGeometry(1, 24, 12), lamps, s * lx, ly, lz)
    lamp.scale.set(lw / 2, lh, 0.12)
    lamp.rotation.y = -s * 0.35
  }
  if (!spec.fenders) {
    const nose = spec.body[spec.body.length - 1]
    const grille = add(new THREE.PlaneGeometry(nose.w * 1.1, nose.h * 1.1), materials.grille, 0, nose.cy - nose.h * 0.15, frontZ + 0.004)
    grille.renderOrder = 1
  }
  const [ry, rz, rw] = spec.lights.rear
  const tail = add(new THREE.BoxGeometry(rw, 0.05, 0.04), materials.tail, 0, ry, rz - 0.01)
  tail.rotation.y = Math.PI

  // Rear wing.
  if (spec.wing) {
    const w = spec.wing
    add(wingElement(w.span, w.chord, 0.05, { camber: 0.1 }), primary, 0, w.y, w.z)
    add(wingElement(w.span, w.chord * 0.5, 0.03, { camber: 0.14 }), secondary, 0, w.y + 0.1, w.z - w.chord * 0.45)
    for (const s of [1, -1]) add(plate(w.endplate, 0.018), trim, s * (w.span / 2 + 0.01), 0, 0)
    if (w.necks) {
      for (const s of [1, -1]) add(plate([[w.z - 0.05, w.y + 0.02], [w.z + 0.25, w.y - 0.42], [w.z + 0.4, w.y - 0.42], [w.z + 0.1, w.y + 0.02]], 0.02), materials.carbon, s * 0.42, 0, 0)
    }
  }
  if (spec.fin) add(plate(spec.fin, 0.012), trim)

  // Mirrors.
  const [mx, my, mz] = spec.mirrors
  for (const s of [1, -1]) car.add(mirror(primary, V(s * (mx - 0.12), my - 0.03, mz), V(s * mx, my, mz), [0.18, 0.1, 0.09]))

  // Wheels in dark wheel wells.
  const wheels: THREE.Group[] = []
  const t = spec.tyre
  const rimMat = spec.coloredRims ? paintMaterial(livery.accent) : undefined
  for (const [z, width] of [[spec.axles[0], t.front], [spec.axles[1], t.rear]] as const) {
    for (const side of [1, -1] as const) {
      const { pivot, spin } = wheel({ radius: t.radius, width, rimRadius: t.rim, spokes: t.spokes, band: livery.accent, rimMaterial: rimMat }, side)
      pivot.position.set(side * spec.track, t.radius, z)
      car.add(pivot)
      wheels.push(spin)
      // A dark well on the body side, just behind the tyre, reads as the arch.
      const rows = sampleSections(spec.fenders ? (z > 0 ? spec.fenders.front : spec.fenders.rear) : spec.body, 80)
      const row = rows.reduce((a, b) => (Math.abs(b.z - z) < Math.abs(a.z - z) ? b : a))
      const surface = (row.cx ?? 0) + row.w
      if (surface < spec.track + width / 2) {
        const well = add(new THREE.CircleGeometry(t.radius + 0.05, 40), materials.dark, side * (surface + 0.004), t.radius + 0.01, z)
        well.rotation.y = side * Math.PI / 2
      }
    }
  }

  // Number on the nose (prototypes) or the bonnet.
  const number = numberDecal(livery.number, 0.34, livery.panel ?? '#F7F7F5')
  const noseSection = spec.body[spec.body.length - 2]
  number.position.set(0, noseSection.cy + noseSection.h + 0.012, noseSection.z)
  number.rotation.x = -Math.PI / 2 + 0.12
  car.add(number)

  return { group: car, wheels, lamps: [lamps], dispose: () => [bodyTex, trimTex].forEach((tx) => tx.dispose()) }
}
